import React, { useCallback } from "react";
import { InvoiceStatus } from "@common/types/invoice";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCreateInvoices } from "@api/invoice.api";
import { AccrualImporterDrawer } from "@components/accrualImporter/AccrualImporterDrawer";
import { AccrualImporterGroup } from "@components/accrualImporter/useAccrualImporterState";
import { mapGroupToAccrualItem } from "@components/accrualImporter/mapGroupsToAccrualItems";
import { AccrualImporterRow } from "@components/accrualImporter/types";

interface Props {
    open?: boolean;
    onClose: () => void;
    initialRows?: AccrualImporterRow[];
}

export const ManualInvoiceCreationDrawer: React.FC<Props> = ({
    open,
    onClose,
    initialRows,
}) => {
    const { workspaceId } = useScope();
    const createInvoices = useCreateInvoices();
    const { toast, error } = useToaster();

    const handleSubmit = useCallback(
        async (groups: AccrualImporterGroup[]) => {
            if (!workspaceId) {
                return;
            }

            try {
                await createInvoices.mutateAsync({
                    workspaceId,
                    invoices: groups.map((group) => {
                        const item = mapGroupToAccrualItem(group);
                        return {
                            ...item,
                            status: InvoiceStatus.OPEN,
                            lines: item.lines.map(
                                ({ accrualAccountId, ...line }) => ({
                                    ...line,
                                    accountsReceivableId: accrualAccountId,
                                }),
                            ),
                        };
                    }),
                });
                toast("Invoices imported");
                onClose();
            } catch {
                error("Failed to import invoices");
            }
        },
        [workspaceId, createInvoices, toast, error, onClose],
    );

    return (
        <AccrualImporterDrawer
            mode="invoice"
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            isSubmitting={createInvoices.isPending}
            initialRows={initialRows}
        />
    );
};
